import { ALLY_STRIKE_MS, RAID_LEVEL, RAID_MAX_PLAYERS, RAID_MIN_PLAYERS, SHEFIRA_ABILITIES, SHEFIRA_ENRAGE_HASTE, SHEFIRA_FIRE_RING, SHEFIRA_FLIGHT, SHEFIRA_THRESHOLDS, STORY_HAUL_MS, } from '../data/shefira.js';
import { ringDistance } from '../world/grid.js';
import { mitigation } from './combat.js';
/**
 * Shefira's fight (ADR 0068 §3), as pure rules: her phase by her health,
 * whether she may be struck, her shapes and how often, when she comes down,
 * how the ring of fire closes, how a raid's size scales her, when the
 * story's allies fall, and who may enter the Aire. The server's
 * `ShefiraDirector` keeps the clock; nothing here keeps any state.
 */
/** On the ground (100 → 60 %), in the sky (60 → 30 %), enraged (30 → 0 %). */
export const SHEFIRA_PHASES = ['ground', 'flight', 'enraged'];
/** Her phase at this health out of `maxHp`. */
export function shefiraPhaseOf(hp, maxHp) {
    if (maxHp <= 0)
        return 'enraged';
    const share = hp / maxHp;
    if (share > SHEFIRA_THRESHOLDS.flight)
        return 'ground';
    if (share > SHEFIRA_THRESHOLDS.enraged)
        return 'flight';
    return 'enraged';
}
/** The health share a phase starts at: where a game master's « go to phase » puts her. */
export function phaseStartShare(phase) {
    switch (phase) {
        case 'ground':
            return 1;
        case 'flight':
            return SHEFIRA_THRESHOLDS.flight;
        case 'enraged':
            return SHEFIRA_THRESHOLDS.enraged;
    }
}
/** Out of reach in the sky: no blow, no spell lands on her until she comes down. */
export function shefiraTargetable(airborne) {
    return !airborne;
}
/** Her health per raider: a full raid of ten a little under three minutes of their blows. */
export const RAID_HEALTH_PER_PLAYER = 11_500;
/** The side of a party from which the second shape comes. */
export const TWIN_SHAPES_FROM = 6;
export function raidScaling(players) {
    const n = Math.min(RAID_MAX_PLAYERS, Math.max(RAID_MIN_PLAYERS, Math.round(players)));
    return {
        players: n,
        health: RAID_HEALTH_PER_PLAYER * n,
        damage: 1,
        shapes: 1,
        rainBonus: Math.floor((n - RAID_MIN_PLAYERS) / 2),
        whelps: 1 + Math.floor(n / 3),
        twinShapes: n >= TWIN_SHAPES_FROM,
    };
}
/**
 * The story (ADR 0068 §4): one Warrior of level 20 and the Company's allies.
 * Three minutes of her health with the Company striking beside, her bite
 * at half and her shapes at about a fifth of the raid's multipliers besides,
 * where they stood before the raid's were raised (a raid's shape takes more
 * than half a Warrior fresh from the Peaks: the story teaches the fight, the
 * raid punishes it), rains a drop lighter, one whelp at a time. Checked by the
 * simulation in `shefira.test.ts`: won with neither potion nor second
 * spell, the player below half their health at the worst.
 */
export const STORY_SCALING = {
    players: 1,
    health: 15_000,
    damage: 0.5,
    shapes: 0.22,
    rainBonus: -1,
    whelps: 1,
    twinShapes: false,
};
export function aerieScaling(mode, players) {
    return mode === 'story' ? STORY_SCALING : raidScaling(players);
}
/**
 * What a shape (or the ring of fire) of this `multiplier` deals before the
 * target's defence, her `bite` being her fight's (already scaled by `damage`).
 */
export function shapeRaw(bite, multiplier, scaling) {
    return Math.max(0, bite * multiplier * scaling.shapes);
}
/**
 * The share of a raider's greatest health one of her shapes (or the ring of
 * fire) of this `multiplier` takes in a raid, whatever they wear (ADR 0068
 * §3): a telegraphed mechanic is dodged, not tanked. Gear at +10 lifts a
 * Warrior's health and defence so far above the same gear at +0 that no
 * blow measured through defence can be felt by the first without felling
 * the second; a share of health is felt alike by both. A dive takes 60 %,
 * a breath 56 %, two big ones in a row kill; Ulfar's veil still halves it.
 */
export const RAID_SHAPE_SHARE_PER_MULTIPLIER = 0.043;
export function raidShapeShare(multiplier) {
    return Math.max(0, multiplier * RAID_SHAPE_SHARE_PER_MULTIPLIER);
}
/**
 * What it deals to a target with `defence`, as `ZoneRoom` applies it to a
 * player (her level is the raid's): at least a point, like any blow of a
 * creature (`mobDamage`).
 */
export function shapeDamage(bite, multiplier, scaling, defence) {
    const raw = shapeRaw(bite, multiplier, scaling);
    if (raw <= 0)
        return 0;
    return Math.max(1, Math.round(raw * (1 - mitigation(defence, RAID_LEVEL))));
}
/**
 * Her shapes in a phase, sized by the fight: a rain gains the raid's drops,
 * and enraged, every shape comes back sooner.
 */
export function shefiraShapes(phase, scaling) {
    const base = phase === 'flight' ? SHEFIRA_ABILITIES.flight : SHEFIRA_ABILITIES.ground;
    return base.map((ability) => ({
        ...ability,
        ...(ability.count !== undefined
            ? { count: Math.max(1, ability.count + scaling.rainBonus) }
            : {}),
        everyMs: phase === 'enraged' ? Math.round(ability.everyMs * SHEFIRA_ENRAGE_HASTE) : ability.everyMs,
    }));
}
/**
 * Whether two harpoons pin her down: from two opposite ballistae (`opposite`
 * names each one's partner across the arena), both within the window.
 */
export function pinned(harpoons, opposite, now) {
    const recent = harpoons.filter((h) => now - h.at <= SHEFIRA_FLIGHT.harpoonWindowMs);
    return recent.some((a) => recent.some((b) => a !== b && opposite.get(a.station) === b.station));
}
/**
 * Whether she comes down now, having risen at `airborneAt`: pinned by two
 * harpoons, hauled down by Nella's (the story: `hauledAt`, when it bit,
 * `STORY_HAUL_MS` before), or tired of the sky after `maxAirborneMs`.
 */
export function landingCause(airborneAt, harpoons, opposite, now, hauledAt = null) {
    if (pinned(harpoons, opposite, now))
        return 'harpooned';
    if (hauledAt !== null && now - hauledAt >= STORY_HAUL_MS)
        return 'harpooned';
    if (now - airborneAt >= SHEFIRA_FLIGHT.maxAirborneMs)
        return 'tired';
    return null;
}
/**
 * The ring of fire of phase 3: the `k`-th one (from 0) spares this many
 * rings round her, one less each time, never under `lastSafe`.
 */
export function fireRingSafe(k) {
    return Math.max(SHEFIRA_FIRE_RING.lastSafe, SHEFIRA_FIRE_RING.firstSafe - SHEFIRA_FIRE_RING.step * Math.max(0, k));
}
/** Whether a tile burns when the ring lands: beyond the safe disc round her. */
export function inFireRing(centre, safe, tile) {
    return ringDistance(centre, tile) > safe;
}
/** One ally's blow on the strike beat (`ALLY_STRIKE_MS`), before her defence. */
export function allyBlow(ally) {
    return Math.max(0, Math.round((ally.dps * ALLY_STRIKE_MS) / 1000));
}
/**
 * The allies a blow carried her past, from `before` to `after` health out of
 * `maxHp`, leaving out those already `fallen`: in the order they fall, the
 * highest threshold first. A blow on a corpse carries nobody.
 */
export function alliesFalling(allies, before, after, maxHp, fallen) {
    if (maxHp <= 0)
        return [];
    return allies
        .filter((ally) => {
        if (fallen.has(ally.arena))
            return false;
        const line = maxHp * ally.fallsAt;
        return before > line && after <= line;
    })
        .sort((a, b) => b.fallsAt - a.fallsAt);
}
/**
 * The story opens to its quest's holder alone, while the stand is still to
 * be made: done once, it is the raid's to fight.
 */
export function storyEntryRefusal(quest) {
    if (!quest.active || quest.stood)
        return 'no-quest';
    return null;
}
/**
 * Whether a raid may go in: its leader asks, three to ten, every one of
 * them level 20, their story made and present at the crevasse. The first
 * member who holds it back is named.
 */
export function raidEntryRefusal(members) {
    if (members.length < RAID_MIN_PLAYERS)
        return { reason: 'too-few' };
    if (members.length > RAID_MAX_PLAYERS)
        return { reason: 'too-many' };
    for (const member of members) {
        if (!member.present)
            return { reason: 'absent', name: member.name };
    }
    for (const member of members) {
        if (member.level < RAID_LEVEL)
            return { reason: 'level', name: member.name };
    }
    for (const member of members) {
        if (!member.stood)
            return { reason: 'not-stood', name: member.name };
    }
    return null;
}
//# sourceMappingURL=shefira.js.map